import { makeStyles, Snackbar, SnackbarCloseReason } from "@material-ui/core"
import Alert, { Color } from "@material-ui/lab/Alert"
import { useCallback, useEffect, useMemo, useState } from "react"
import { queryClient } from "../../App"
import {
  ClientFragment,
  useDeleteAssessmentQuestionCustomPromptMutation,
  useGetAssessmentQuestionCustomPromptsByClientIdQuery,
  useUpdateAssessmentQuestionCustomPromptHeaderMutation,
  useUpsertAssessmentQuestionCustomPromptMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useSession } from "../../hooks/useSession"
import { Role } from "../../utils/constants"
import Button from "../atoms/button/Button"
import DisabledActionTooltip from "../molecules/DisabledActionTooltip"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

const useStyles = makeStyles({
  customUrgesPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    marginTop: 8,
    marginBottom: 8,
  },
  customUrgesTitle: {
    margin: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    fontSize: 18,
    color: "#737373",
  },
  customUrgesInputsRow: {
    display: "flex",
    justifyContent: "center",
    gap: 24,
    width: "100%",
  },
  customUrgeInput: {
    minWidth: 300,
    padding: "10px 14px",
    borderRadius: 4,
    border: "1px solid #E0E0E0",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    color: "#4F4F4F",
    backgroundColor: "#FFFFFF",
  },
  customUrgesHeadersRow: {
    display: "flex",
    justifyContent: "center",
    gap: 24,
    width: "100%",
  },
  customUrgeHeader: {
    minWidth: 300,
    padding: "10px 14px",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E0E0",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    color: "#4F4F4F",
  },
})

type CustomUrgesContentProps = {
  client: ClientFragment
  canSchedule?: boolean
}

/**
 * Renders the Custom Urges panel, allowing practitioners to view and edit up to
 * three custom urge prompts and their result-table headers for a client.
 *
 * The component:
 * - Initializes state from existing assessment question custom prompts.
 * - Tracks both current and initial values to avoid unnecessary mutations.
 * - Persists changes via GraphQL mutations, handling header-only updates,
 *   soft-deletes for cleared prompts, and upserts for new or changed entries.
 * - Invalidates the `GetAssessmentQuestionCustomPrompts` query after mutations
 *   to refresh cached data.
 *
 * @param props - Component props.
 * @param props.client - The client for whom the custom urges are managed.
 */

const CustomUrgesContent = (props: CustomUrgesContentProps) => {
  const classes = useStyles()
  const graphqlClient = useGraphQLClient()
  const { session } = useSession()
  const isSupervisorReadOnly = session?.role === Role.WEB_ROLE
  const isDisconnectedReadOnly = props.canSchedule === false
  const isReadOnly = isSupervisorReadOnly || isDisconnectedReadOnly
  const updateCustomPromptHeaderMutation =
    useUpdateAssessmentQuestionCustomPromptHeaderMutation(graphqlClient)
  const deleteCustomPromptMutation =
    useDeleteAssessmentQuestionCustomPromptMutation(graphqlClient)
  const upsertCustomPromptMutation =
    useUpsertAssessmentQuestionCustomPromptMutation(graphqlClient)

  const [customUrges, setCustomUrges] = useState<string[]>(["", "", ""])
  const [customUrgeHeaders, setCustomUrgeHeaders] = useState<string[]>([
    "",
    "",
    "",
  ])
  const [initialCustomUrges, setInitialCustomUrges] = useState<string[]>([
    "",
    "",
    "",
  ])
  const [initialCustomUrgeHeaders, setInitialCustomUrgeHeaders] = useState<
    string[]
  >(["", "", ""])
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })
  const { data: customPromptData } =
    useGetAssessmentQuestionCustomPromptsByClientIdQuery(graphqlClient, {
      clientId: props.client.id,
    })
  // Three special assessment question IDs that represent the custom urges
  const customUrgePromptQuestionIds: string[] = useMemo(
    () => [
      "73de75e4-1d57-4177-8e09-0c1fc73b5afe",
      "28fba1ff-9792-49f1-a6a7-f43e2063e7ea",
      "84adc27a-d92b-45aa-a911-cbbe5328d159",
    ],
    []
  )

  // Initialize the three custom-urge boxes from assessment_question_custom_prompt
  useEffect(() => {
    if (!customPromptData) return

    const promptsByQuestionId = new Map<
      string,
      (typeof customPromptData.assessment_question_custom_prompt)[number]
    >()

    for (const prompt of customPromptData.assessment_question_custom_prompt) {
      if (prompt.deleted_at) continue
      const qid = String(prompt.assessment_question_id)
      if (!customUrgePromptQuestionIds.includes(qid)) continue
      if (!promptsByQuestionId.has(qid)) {
        promptsByQuestionId.set(qid, prompt)
      }
    }

    if (promptsByQuestionId.size === 0) return

    setCustomUrges((prev) => {
      const next = [...prev]
      customUrgePromptQuestionIds.forEach((qid, index) => {
        const prompt = promptsByQuestionId.get(qid)
        if (prompt && !next[index]?.trim()) {
          next[index] = prompt.text
        }
      })
      return next
    })

    setInitialCustomUrges((prev) => {
      const next = [...prev]
      customUrgePromptQuestionIds.forEach((qid, index) => {
        const prompt = promptsByQuestionId.get(qid)
        if (prompt) {
          next[index] = prompt.text
        }
      })
      return next
    })

    setCustomUrgeHeaders((prev) => {
      const next = [...prev]
      customUrgePromptQuestionIds.forEach((qid, index) => {
        const prompt = promptsByQuestionId.get(qid)
        if (prompt && !next[index]?.trim()) {
          next[index] = prompt.header ?? ""
        }
      })
      return next
    })

    setInitialCustomUrgeHeaders((prev) => {
      const next = [...prev]
      customUrgePromptQuestionIds.forEach((qid, index) => {
        const prompt = promptsByQuestionId.get(qid)
        if (prompt) {
          next[index] = prompt.header ?? ""
        }
      })
      return next
    })
  }, [customPromptData, customUrgePromptQuestionIds])

  const handleCustomUrgeChange = useCallback((index: number, next: string) => {
    setCustomUrges((prev) => {
      const copy = [...prev]
      copy[index] = next
      return copy
    })
  }, [])

  const handleCustomUrgeHeaderChange = useCallback(
    (index: number, next: string) => {
      setCustomUrgeHeaders((prev) => {
        const copy = [...prev]
        copy[index] = next
        return copy
      })
    },
    []
  )

  const handleSaveCustomUrges = useCallback(async () => {
    let anyChangePersisted = false

    for (let index = 0; index < customUrges.length; index++) {
      const questionId = customUrgePromptQuestionIds[index]
      const text = customUrges[index]?.trim()
      const header = customUrgeHeaders[index]?.trim()

      const initialText = initialCustomUrges[index]?.trim()
      const initialHeader = initialCustomUrgeHeaders[index]?.trim()
      const headerChanged = (header ?? "") !== (initialHeader ?? "")

      // No changes to persist
      if (text === initialText && header === initialHeader) {
        continue
      }

      if (!session?.userId) {
        console.error("Cannot save custom urge: missing practitioner session")
        return
      }

      const nowIso = new Date().toISOString()

      // Require client-facing text before allowing a header-only save attempt.
      if (!text && header) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Client display text is required to save an urge",
        })
        continue
      }

      // If only the header changed (text stayed the same), update the
      // existing active prompt in place instead of soft-deleting and
      // inserting a new record. This keeps the same prompt ID so any
      // assessment answers linked to it via customPromptId continue to
      // reference the original record.
      if (text === initialText && headerChanged) {
        try {
          await updateCustomPromptHeaderMutation.mutateAsync({
            assessment_question_id: questionId,
            client_id: props.client.id,
            header: header || null,
          })

          setInitialCustomUrgeHeaders((prev) => {
            const next = [...prev]
            next[index] = header || ""
            return next
          })

          anyChangePersisted = true

          setSnackbar({
            open: true,
            severity: "success",
            message: "Custom urge header saved",
          })

          await queryClient.invalidateQueries({
            queryKey: ["GetAssessmentQuestionCustomPrompts"],
          })
        } catch (error) {
          console.error(
            "Failed to update custom urge prompt header in place",
            error
          )

          setSnackbar({
            open: true,
            severity: "error",
            message: "Failed to save custom urge header",
          })
        }

        continue
      }

      if (!text) {
        // If there was nothing previously saved, there's nothing to delete.
        if (!initialText && !initialHeader) {
          continue
        }

        try {
          await deleteCustomPromptMutation.mutateAsync({
            assessment_question_id: questionId,
            client_id: props.client.id,
            deleted_at: nowIso,
          })

          setSnackbar({
            open: true,
            severity: "success",
            message: "Custom urge deleted",
          })

          setInitialCustomUrges((prev) => {
            const next = [...prev]
            next[index] = ""
            return next
          })
          setInitialCustomUrgeHeaders((prev) => {
            const next = [...prev]
            next[index] = ""
            return next
          })
          setCustomUrges((prev) => {
            const next = [...prev]
            next[index] = ""
            return next
          })
          setCustomUrgeHeaders((prev) => {
            const next = [...prev]
            next[index] = ""
            return next
          })

          anyChangePersisted = true

          await queryClient.invalidateQueries({
            queryKey: ["GetAssessmentQuestionCustomPrompts"],
          })
        } catch (error) {
          console.error("Failed to delete custom urge prompt", error)

          setSnackbar({
            open: true,
            severity: "error",
            message: "Failed to delete custom urge",
          })
        }

        continue
      }

      try {
        await upsertCustomPromptMutation.mutateAsync({
          assessment_question_id: questionId,
          client_id: props.client.id,
          practitioner_id: session.userId,
          text: text,
          header: header || null,
          deleted_at: nowIso,
        })

        // Update local "initial" snapshots so subsequent saves only fire on further changes
        setInitialCustomUrges((prev) => {
          const next = [...prev]
          next[index] = text
          return next
        })
        setInitialCustomUrgeHeaders((prev) => {
          const next = [...prev]
          next[index] = header || ""
          return next
        })

        anyChangePersisted = true

        setSnackbar({
          open: true,
          severity: "success",
          message: "Custom urge saved",
        })

        await queryClient.invalidateQueries({
          queryKey: ["GetAssessmentQuestionCustomPrompts"],
        })
      } catch (error) {
        console.error("Failed to save custom urge prompt", error)

        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to save custom urge",
        })
      }
    }

    if (!anyChangePersisted) {
      setSnackbar({
        open: true,
        severity: "info",
        message: "No changes to save",
      })
    }
  }, [
    props.client.id,
    customUrgeHeaders,
    customUrgePromptQuestionIds,
    customUrges,
    initialCustomUrgeHeaders,
    initialCustomUrges,
    session?.userId,
    updateCustomPromptHeaderMutation,
    deleteCustomPromptMutation,
    upsertCustomPromptMutation,
  ])

  const handleCloseSnackbar = (
    event: React.SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }
  return (
    <div className={classes.customUrgesPanel}>
      <h2 className={classes.customUrgesTitle}>Custom Urges</h2>
      <span>Text to display to client (leave blank to hide)</span>
      <div className={classes.customUrgesInputsRow}>
        {customUrges.map((value, index) => (
          <input
            key={index}
            className={classes.customUrgeInput}
            placeholder={`Question text for Urge ${index + 1}`}
            value={value}
            readOnly={isReadOnly}
            onChange={(event) =>
              handleCustomUrgeChange(index, event.target.value)
            }
          />
        ))}
      </div>
      <span>
        Text to display in results table below (optional, default is question
        text)
      </span>
      <div className={classes.customUrgesHeadersRow}>
        {customUrges.map((_, index) => (
          <input
            key={index}
            className={classes.customUrgeHeader}
            placeholder={`Results column for Urge ${index + 1}`}
            value={customUrgeHeaders[index]}
            readOnly={isReadOnly}
            onChange={(event) =>
              handleCustomUrgeHeaderChange(index, event.target.value)
            }
          />
        ))}
      </div>
      {!isSupervisorReadOnly && (
        <DisabledActionTooltip
          disabled={isDisconnectedReadOnly}
          text={SCHEDULING_DISABLED_TOOLTIP}
        >
          <Button
            customVariant="secondary"
            style={{ minWidth: 300 }}
            disabled={isDisconnectedReadOnly}
            onClick={() => handleSaveCustomUrges()}
          >
            Save Custom Urges
          </Button>
        </DisabledActionTooltip>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default CustomUrgesContent
